import React, { useState, useEffect } from 'react';
import { Modal, Form, Select, InputNumber } from 'antd';
import { useTranslation } from 'react-i18next';
import { Recruiter } from '../../types'; // Adjust path if needed
import Button from '../../UI/buttons/Button';

const { Option } = Select;

interface SetRecruiterTargetModalProps {
    isVisible: boolean;
    recruiter: Recruiter | null;
    onSave: (targetType: string, targetValue: number) => Promise<void>; // Make async for API call
    onClose: () => void;
}

const SetRecruiterTargetModal: React.FC<SetRecruiterTargetModalProps> = ({
    isVisible,
    recruiter,
    onSave,
    onClose,
}) => {
    const [form] = Form.useForm();
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);

    // Reset form fields when the modal becomes visible or the recruiter changes
    useEffect(() => {
        if (isVisible && recruiter) {
            form.setFieldsValue({
                target_type: recruiter.current_target_type || null, // Default to null if not set
                target_value: recruiter.current_target_value,
            });
        } else {
            form.resetFields();
        }
    }, [isVisible, recruiter, form]);

    const handleSave = async () => {
        try {
            const values = await form.validateFields();
            setLoading(true);
            await onSave(values.target_type, values.target_value);
            // Success message can be handled in the parent component after API call succeeds
            // message.success(t('TARGET_SET_SUCCESS', 'Target updated successfully!'));
            // onClose(); // Closing is handled by parent component after API success
        } catch (errorInfo) {
            console.log('Failed:', errorInfo);
            // message.error(t('TARGET_SET_ERROR', 'Failed to update target.'));
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            title={t('SET_RECRUITER_TARGET_TITLE', `Set Target for ${recruiter?.name || 'Recruiter'}`)}
            visible={isVisible}
            onCancel={onClose}
            footer={[
                <Button key="back" onClick={onClose}>
                    {t('CANCEL', 'Cancel')}
                </Button>,
                <Button key="submit" type="primary" loading={loading} onClick={handleSave}>
                    {t('SAVE', 'Save')}
                </Button>,
            ]}
        >
            <Form form={form} layout="vertical" name="set_recruiter_target_form">
                <Form.Item
                    name="target_type"
                    label={t('TARGET_TYPE_LABEL', 'Target Type')}
                    rules={[{ required: true, message: t('TARGET_TYPE_REQUIRED', 'Please select a target type!') }]}
                >
                    <Select placeholder={t('TARGET_TYPE_PLACEHOLDER', 'Select type')}>
                        <Option value="hires">{t('TARGET_TYPE_HIRES', 'Hires')}</Option>
                        <Option value="introductions">{t('TARGET_TYPE_INTRODUCTIONS', 'Introductions')}</Option>
                    </Select>
                </Form.Item>
                <Form.Item
                    name="target_value"
                    label={t('TARGET_VALUE_LABEL', 'Target Value')}
                    rules={[
                        { required: true, message: t('TARGET_VALUE_REQUIRED', 'Please input the target value!') },
                        { type: 'number', min: 0, message: t('TARGET_VALUE_MIN_ZERO', 'Target must be 0 or greater!') },
                    ]}
                >
                    <InputNumber min={0} style={{ width: '100%' }} placeholder={t('TARGET_VALUE_PLACEHOLDER', 'Enter target number')} />
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default SetRecruiterTargetModal; 